import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { jobsAPI } from '@/services/api';
import JobStatusBadge from '@/components/user/JobStatusBadge';
import EmptyState from '@/components/user/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { VM_UPLOAD_STATUSES, formatDate } from '@/utils/jobStatus';
import { toast } from 'sonner';
import { ChevronRight, Upload } from 'lucide-react';

const VideomakerJobsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data } = await jobsAPI.list({ videomaker_id: user.id });
      setJobs(Array.isArray(data) ? data : data?.jobs || []);
    } catch {
      toast.error('Não foi possível carregar os jobs.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const uploadJobs = useMemo(
    () => jobs.filter((j) => VM_UPLOAD_STATUSES.includes(j.status)),
    [jobs]
  );
  const otherJobs = useMemo(
    () => jobs.filter((j) => !VM_UPLOAD_STATUSES.includes(j.status)),
    [jobs]
  );

  const renderList = (list) => {
    if (loading) {
      return (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full bg-white/10" />
          ))}
        </div>
      );
    }
    if (!list.length) return null;
    return (
      <ul className="space-y-3">
        {list.map((job) => {
          const needsUpload = VM_UPLOAD_STATUSES.includes(job.status);
          return (
            <li key={job.id}>
              <button
                type="button"
                onClick={() => navigate(`/videomaker/jobs/${job.id}`)}
                className="w-full text-left rounded-xl border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{job.titulo || `Job ${job.id?.slice(0, 8)}`}</p>
                    <p className="text-xs text-white/50 mt-1">
                      {formatDate(job.data_gravacao || job.created_at)}
                    </p>
                    {needsUpload && (
                      <Badge className="mt-2 bg-amber-500/20 text-amber-200 border-amber-500/30">
                        <Upload className="h-3 w-3 mr-1 inline" />
                        {job.status === 'revision_requested' ? 'Revisão pendente' : 'Enviar mídias'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <JobStatusBadge status={job.status} />
                    <ChevronRight className="h-4 w-4 text-white/40" />
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Meus jobs</h1>

      {uploadJobs.length > 0 && (
        <section>
          <h2 className="text-sm font-medium text-white/60 mb-3">Precisa de ação</h2>
          {renderList(uploadJobs)}
        </section>
      )}

      <section>
        <h2 className="text-sm font-medium text-white/60 mb-3">Todos</h2>
        {!loading && !jobs.length ? (
          <EmptyState
            title="Nenhum job atribuído"
            description="Jobs aceitos no app aparecerão aqui para envio de mídias."
          />
        ) : (
          renderList(otherJobs.length ? otherJobs : jobs)
        )}
      </section>
    </div>
  );
};

export default VideomakerJobsPage;
